import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and other response issues
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API error:', error.response?.status, error.config?.url);
    
    // Only redirect on 401 for non-auth endpoints (login failures should be handled by the form)
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  getProfile: () => api.get('/auth/profile'),
  verifyToken: () => api.get('/auth/verify'),
};

// Rooms API
export const roomsAPI = {
  getAll: () => api.get('/rooms'),
  getById: (id) => api.get(`/rooms/${id}`),
  create: (data) => api.post('/rooms', data),
  update: (id, data) => api.put(`/rooms/${id}`, data),
  delete: (id) => api.delete(`/rooms/${id}`),
  getFinancials: (id, params) => api.get(`/rooms/${id}/financials`, { params }),
  markVacant: (id) => api.patch(`/rooms/${id}/vacate`),
};

// Meters API
export const metersAPI = {
  getAll: (params) => api.get('/meters', { params }),
  getById: (id) => api.get(`/meters/${id}`),
  create: (data) => api.post('/meters', data),
  update: (id, data) => api.put(`/meters/${id}`, data),
  delete: (id) => api.delete(`/meters/${id}`),
};

// Electricity Bills API
export const electricityAPI = {
  getAll: (params) => api.get('/electricity', { params }),
  getById: (id) => api.get(`/electricity/${id}`),
  create: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
        formData.append(key, data[key]);
      }
    });
    return api.post('/electricity', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  update: (id, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
        formData.append(key, data[key]);
      }
    });
    return api.put(`/electricity/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  delete: (id) => api.delete(`/electricity/${id}`),
  downloadPDF: (id) => api.get(`/electricity/${id}/download`, { responseType: 'blob' }),
};

// Payments API
export const paymentsAPI = {
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
};

// Billing API
export const billingAPI = {
  getRoomSummary: (roomId) => api.get(`/billing/room/${roomId}/summary`),
  generateMonthlyBill: (roomId, data) => api.post(`/billing/monthly/${roomId}`, data),
  getMonthlyBills: (params) => api.get('/billing/monthly', { params }),
  updateMonthlyBill: (id, data) => api.put(`/billing/monthly/${id}`, data),
  deleteMonthlyBill: (id) => api.delete(`/billing/monthly/${id}`),
  downloadMonthlyBill: (id) => api.get(`/billing/monthly/${id}/download`, { responseType: 'blob' }),
  recordPayment: (data) => api.post('/billing/payment', data),
};

// Reports API
export const reportsAPI = {
  getDashboard: (params) => api.get('/reports/dashboard', { params }),
  getSummary: (params) => api.get('/reports/summary', { params }),
  getFinancialSummary: (params) => api.get('/reports/financial-summary', { params }),
  getPaymentHistory: (params) => api.get('/reports/payment-history', { params }),
  getElectricityUsage: (params) => api.get('/reports/electricity-usage', { params }),
  getElectricityTrends: (params) => api.get('/reports/electricity-trends', { params }),
  exportData: (params) => api.get('/reports/export', { params, responseType: 'blob' }),
  downloadReport: (type, params) => {
    const endpoint = type === 'detailed' ? '/reports/download/detailed' :
                    type === 'arrears' ? '/reports/download/arrears' :
                    type === 'tax' ? '/reports/download/tax' : '/reports/download/detailed';
    return api.get(endpoint, { params, responseType: 'blob' });
  },
};

// Analytics API
export const analyticsAPI = {
  getOccupancyTimeline: (params) => api.get('/analytics/occupancy-timeline', { params }),
  getYearOverYear: (params) => api.get('/analytics/year-over-year', { params }),
  getPaymentBehavior: (params) => api.get('/analytics/payment-behavior', { params }),
  getElectricityAnomalies: (params) => api.get('/analytics/electricity-anomalies', { params }),
  getCashFlow: (params) => api.get('/analytics/cash-flow', { params }),
  getTenantLongevity: () => api.get('/analytics/tenant-longevity'),
};

// Security Deposits API
export const depositsAPI = {
  getSummary: () => api.get('/deposits/summary'),
  getAll: (params) => api.get('/deposits', { params }),
  getById: (id) => api.get(`/deposits/${id}`),
  create: (data) => api.post('/deposits', data),
  update: (id, data) => api.put(`/deposits/${id}`, data),
  delete: (id) => api.delete(`/deposits/${id}`),
};

export default api;
